/**
 * @module Support
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
 /* tslint:disable:no-any */ // Need to support all data types
 import { Injectable } from '@angular/core';
 import { API_MAP } from '@core/api/proxies/common-api-endpoints';
 import { ApiGatewayService } from '@core/api/services/api-gateway-service';
 import { LoggerService } from '@core/logger/services/logger.service';
 import { APIError } from '@support/api/common/error.interface';
 import { AddressValidationRequest } from '@support/api/proxies/requests/api-2004.interface';
 import { Address, AddressValidationResponse } from '@support/api/proxies/responses/api-2004.interface';
 import { UpdateAddressResponse } from '@support/api/proxies/responses/api-2802.interface';
 import { ADDRESS_SUPPORT_SERVICE } from '@support/cart-support/constants/cart-support-service-constants';
 import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
 import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
 import { BillingTypeEnum
 } from '@experience/shop/shared-browse/constants/shared-browse.constants';
 import {of as observableOf, throwError as observableThrowError,  Observable } from 'rxjs';
 import {catchError, map} from 'rxjs/operators';
 import { HttpResponse, HttpHeaders } from '@angular/common/http';
 
 @Injectable()
 export class AddressValidateService {
 
   /**
    * initial configuration of component
    */
   constructor(private logger: LoggerService,
               private apiGatewayService: ApiGatewayService,
               private stateStoreUtilsService: StateStoreUtilsService,
               private objectService: ObjectService) {
   }
 
   /**
    * Method to validate the address from the API
    * @param form: Stores the value of address to be passed
    * @returns AddressValidationResponse
    */
   public validateAddress(form: Address): Observable<Address[]> {
     const request = this.getAddressVerificationRequest(form);
     let headers = new HttpHeaders();
     const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
     const billingTypeEnum =   this.objectService.get(customerConfigData, 'billingType', 0) || 0;
     const billingType =  BillingTypeEnum[billingTypeEnum].toLowerCase();
     headers = headers.append('billingType', billingType);
     return this.apiGatewayService.postRequest(API_MAP.API_2004_addressValidation, request, headers,
       null, null).pipe(map((response: HttpResponse<any>) => {
       this.updateCustomerAddressData(form);
       return this.filterSuggestedAddress(response.body);
     }), catchError((error: APIError) => {
       this.logger.error(ADDRESS_SUPPORT_SERVICE.ADDRESS_MODULE_NAME,
         ADDRESS_SUPPORT_SERVICE.ADDRESS_SUPPORT_SERVICE,
         ' API error for postRequest for validateAddress() in '
         + ADDRESS_SUPPORT_SERVICE.ADDRESS_SUPPORT_SERVICE + ' error: ' + JSON.stringify(error), error.code);
       return observableThrowError(error);
     }));
   }
 
   /**
    * Method to update the address details from the API service
    * @param addressDetails: Stores the value of the updated address model
    * @param addressType: Strores the string type for the address type
    * @returns UpdateAddressResponse
    */
   public updateAddressDetails(addressDetails: Address,
                               addressType: string): Observable<UpdateAddressResponse> {
     addressDetails.addressType = addressType;
     const headers = new HttpHeaders();
     const customerConfigData = this.stateStoreUtilsService.getCustomerConfigData();
 
     if (customerConfigData && customerConfigData.isAccountSetup && this.isExistingAddress(addressDetails)) {
       return observableOf(<UpdateAddressResponse>new Object());
     } else {
       const profileId = this.objectService
         .get(this.stateStoreUtilsService.getCustomerConfigData(), 'creditCheckResponse.commonCustomerId');
       const pathParms = {profileId: profileId};
       return this.apiGatewayService.postRequest(API_MAP.API_2802_updateAddress,
         addressDetails, headers, null, pathParms).pipe(
         map((response: HttpResponse<UpdateAddressResponse>) => {
           this.logger.debug(ADDRESS_SUPPORT_SERVICE.ADDRESS_MODULE_NAME,
             ADDRESS_SUPPORT_SERVICE.ADDRESS_SUPPORT_SERVICE,
             'received response for update Address Details  ' + ADDRESS_SUPPORT_SERVICE.ADDRESS_SUPPORT_SERVICE +
             ' response : ' + JSON.stringify(response));
           return response.body;
         }), catchError((error: APIError) => {
           this.logger.error(ADDRESS_SUPPORT_SERVICE.ADDRESS_MODULE_NAME,
             ADDRESS_SUPPORT_SERVICE.ADDRESS_SUPPORT_SERVICE,
             ' API error for postRequest for updateAddressDetails() in '
             + ADDRESS_SUPPORT_SERVICE.ADDRESS_SUPPORT_SERVICE + ' error: ' + JSON.stringify(error), error.code);
           return observableThrowError(error);
         }));
     }
   }
 
   /** Get address verification request
    * @returns AddressValidationRequest
    */
   private getAddressVerificationRequest(form: Address): AddressValidationRequest {
       return {
         addressLine1: this.objectService.get(form, 'addressLine1'),
         addressLine2: this.objectService.get(form, 'addressLine2'),
         cityName: this.objectService.get(form, 'cityName'),
         stateCode: this.objectService.get(form, 'stateCode'),
         postalCode: this.objectService.get(form, 'postalCode'),
         urbanization: this.objectService.get(form, 'urbanization')
         || this.objectService.get(form, 'urbanizationCode', ''),
         validationPurpose: this.objectService.get(form, 'validationPurpose', '')
   };
   }
 
   /**
    * Method to update Customer information in Config Data after Credit check is performed
    * @param form : Form which will be filled during any address flow
    * @returns void
    */
   private updateCustomerAddressData(form: Address): void {
     const customerConfigData =
       this.stateStoreUtilsService.getCustomerConfigData() || {};
     customerConfigData.addresses = {
       addressLine1: form.addressLine1,
       addressLine2: form.addressLine2,
       postalCode: form.postalCode,
       stateCode: form.stateCode,
       cityName: form.cityName
     };
   }
 
   /**
    * checks if the address already exists before updating it
    * @param address address object
    * @returns boolean
    */
   private isExistingAddress(address: Address): boolean {
     const currentAddress = this.objectService.get(this.stateStoreUtilsService.getCustomerConfigData(),
       'addresses');
     return (`${address.addressLine1}${address.addressLine2}${address.cityName}${address.stateCode}${address.postalCode}`
       === `${currentAddress.addressLine1}${currentAddress.addressLine2}${currentAddress.cityName}${currentAddress
         .stateCode}${currentAddress.postalCode}`);
   }
 
 
   /**
    * Function filters suggested addresses before returning data. The original address is identified by
    * address1.overrideIndicator === 'true', hence it would sort to the first value on the list
    * @param addresses All suggested addresses from API_2004 response
    * @returns SuggestedAddress as an array object
    */
   private filterSuggestedAddress(address: AddressValidationResponse): Address[] {
     return address.addresses.sort((address1: Address, address2: Address) => {
       // If address1 has overrideIndicator === 'true', then it should come first
       if (address1.overrideIndicator === 'true') {
         return (address2.overrideIndicator === 'true') ? 0 : -1;
       } else {
         return (address2.overrideIndicator === 'true') ? 1 : 0;
       }
     });
   }
 }
 